import { useLayoutEffect, useRef, type RefObject } from 'react';
import {
  registerKeybindingTarget,
  type KeybindingEditor,
  type KeybindingHandlersRef,
} from '../../../keybindings/dispatch/targetRegistry';

export const QUERY_CANCEL_KEYBINDING_COMMAND_IDS = ['query.cancel'] as const;

type QueryCancelEditor = {
  readonly hasTextFocus: () => boolean;
};

type UseQueryCancelKeybindingOptions = {
  readonly tabId: string;
  readonly editorRef: RefObject<QueryCancelEditor | null>;
  readonly rootRef: RefObject<HTMLElement | null>;
  readonly isExecuting: boolean;
  readonly onCancel: () => void;
};

/**
 * The tab's root while its workspace host shows it. Inactive workspace tabs
 * stay mounted inside a `.hidden` host; a hidden tab's root does not count as
 * visible or eligible (no focusless editor, no scoped root). Shared by the
 * Query Editor tab's keybinding registrations.
 */
export function visibleTabRoot(root: HTMLElement | null): HTMLElement | null {
  return root && !root.closest('.hidden') ? root : null;
}

function editorDomNode(root: HTMLElement | null): HTMLElement | null {
  if (!root) return null;
  if (root.matches('.monaco-editor')) return root;
  return root.querySelector<HTMLElement>('.monaco-editor');
}

export function createQueryEditorKeybindingEditor(
  getEditor: () => QueryCancelEditor | null,
  getRoot: () => HTMLElement | null,
): KeybindingEditor | null {
  const editor = getEditor();
  if (!editor) return null;
  return Object.assign(editor, {
    getDomNode: () => editorDomNode(getRoot()),
  });
}

export function useQueryCancelKeybinding({
  tabId,
  editorRef,
  rootRef,
  isExecuting,
  onCancel,
}: UseQueryCancelKeybindingOptions): void {
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const isExecutingRef = useRef(isExecuting);
  isExecutingRef.current = isExecuting;
  const handlersRef = useRef<KeybindingHandlersRef>({ current: {} });
  handlersRef.current.current = {
    [QUERY_CANCEL_KEYBINDING_COMMAND_IDS[0]]: () => onCancelRef.current(),
  };

  useLayoutEffect(() => registerKeybindingTarget({
    scope: 'query-editor',
    tabId,
    handlers: handlersRef.current,
    getEditor: () => createQueryEditorKeybindingEditor(
      () => editorRef.current,
      () => visibleTabRoot(rootRef.current),
    ),
    getRootElement: () => visibleTabRoot(rootRef.current),
    canExecute: (commandId) => commandId !== 'query.cancel' || isExecutingRef.current,
  }), [editorRef, rootRef, tabId]);
}
